import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { businessLinesApi, type BusinessLineWithDepartments } from '../api/businessLines'
import { GENERAL, setBranch, useBranch } from '../lib/branch'
import { getFeatureFlags } from '../lib/featureFlags'
import { Callout, LoadingState, Page, PageHeader } from '../components/ui'

/**
 * ADR-019 (Equal Point): la pagina d'ingresso. Una tessera per ramo che l'utente può aprire, più la vista
 * Generale per chi la vede; chi ha un solo ramo ci entra direttamente. Con il flag `equalpoint.rami` spento
 * la pagina non esiste (porta alla home di sempre). I numeri per ramo (attivi, in lavorazione, valore del mese)
 * arrivano con l'endpoint dei totali (card 4).
 */
export function EqualPointPage({ fallback = '/dashboard' }: { fallback?: string }) {
  const navigate = useNavigate()
  // null = flag ancora da leggere; poi dove si va dopo la scelta (la home per ruolo, se è accesa).
  const [flags, setFlags] = useState<{ on: boolean; next: string } | null>(null)
  useEffect(() => {
    let alive = true
    void getFeatureFlags().then(f => {
      if (alive) setFlags({ on: f['equalpoint.rami'] === true, next: f['home.per_ruolo'] === true ? '/home' : fallback })
    })
    return () => { alive = false }
  }, [fallback])

  const { mine, branch } = useBranch(flags?.on === true)
  const [lines, setLines] = useState<BusinessLineWithDepartments[] | null>(null)
  const [error, setError] = useState('')
  const choices = mine ? mine.lines.length + (mine.general ? 1 : 0) : 0

  useEffect(() => {
    if (!mine || choices <= 1) return
    let alive = true
    businessLinesApi.list()
      .then(r => { if (alive) setLines(r) })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : 'Errore') })
    return () => { alive = false }
  }, [mine, choices])

  // Un solo ramo e niente Generale: non c'è niente da scegliere, si entra nel ramo.
  const only = mine && !mine.general && mine.lines.length === 1 ? mine.lines[0]?.slug ?? null : null
  useEffect(() => { if (only) setBranch(only, { animate: false }) }, [only])

  if (flags && !flags.on) return <Navigate to={fallback} replace />
  // Nessun ramo da aprire (per esempio rami assegnati ma tutti spenti): si entra nell'app di sempre.
  if (flags && mine && choices <= 1) return <Navigate to={flags.next} replace />
  if (error) return <Page><Callout tone="error">{error}</Callout></Page>
  if (!flags || !mine || !lines) return <Page><LoadingState /></Page>

  const open = (slug: string) => { setBranch(slug); navigate(flags.next) }
  const departments = (id: number) => lines.find(l => l.id === id)?.departments ?? []

  return (
    <Page>
      <PageHeader title="Equal Point" subtitle="Scegli il ramo in cui lavorare: menu e colori seguono il ramo, i clienti sono gli stessi." />
      <div className="ep-tiles">
        {mine.general && (
          <button type="button" className="ep-tile" style={{ ['--ep-color' as string]: '#B8A48C' }}
            aria-pressed={branch === GENERAL} onClick={() => open(GENERAL)}>
            <span className="ep-tile__name">Generale</span>
            <span className="ep-tile__meta">Tutti i rami insieme: clienti, cruscotto e report di tutta la produzione.</span>
          </button>
        )}
        {mine.lines.map(l => {
          const deps = departments(l.id)
          return (
            <button key={l.slug} type="button" className="ep-tile" style={{ ['--ep-color' as string]: l.color }}
              aria-pressed={branch === l.slug} onClick={() => open(l.slug)}>
              <span className="ep-tile__name">{l.name}</span>
              <span className="ep-tile__meta">
                {deps.length > 0 ? deps.map(d => d.name).join(', ') : 'Nessun settore associato'}
              </span>
            </button>
          )
        })}
      </div>
    </Page>
  )
}
